import { navigationLocationId } from '@/lib/tableViewNavigation.mjs'
import { useLocation } from 'react-router-dom'
import useNavigationViews from '@/hooks/useNavigationViews'
import { tableViewNavigationUrl, tableViewRoutes } from '@/lib/tableViewNavigation.mjs'
import useSWR from 'swr'
import { sumBy } from 'lodash'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import AiRingIcon from '@/components/ui/ai/AiRingIcon'
import useMe from '@/hooks/useMe'
import useUserLocation from '@/hooks/useUserLocation'

import {
    Home01,
    Package,
    Container,
    Clock,
    CreditCard01,
    LogIn01,
    HelpSquare,
    Settings01,
    ShoppingCart01,
    // Subitem icons
    List,
    CalendarCheck01,
    CheckDone01,
    PlusCircle,
    Building01,
    ClipboardCheck,
    SwitchHorizontal01,
    PackageCheck,
    Scan,
    Receipt,
    File06,
    Users01,
    ShoppingBag01,
    BarChart01,
    Tag01,
    RefreshCw01,
    XCircle,
    Tag03,
    GitBranch01,
    Settings04,
    Truck01,
    MessageChatCircle,
    Database01,
} from '@untitled-ui/icons-react'

export function useNavigationItems() {
    const { role: userRole, isAdmin } = useMe()

    const { requestGet } = useAxiosApi()

    // Same keys and fetcher as Dashboard.jsx, so the badges and the dashboard
    // share one cached answer instead of each asking. Used to refetch on every
    // navigation; now once a minute, on focus, and whenever a screen calls
    // mutate('/dashboard').
    const swrOptions = { refreshInterval: 60000, dedupingInterval: 30000 }
    const { data: dashboardResponse } = useSWR('/dashboard', requestGet, swrOptions)
    const { data: inventoryResponse } = useSWR('/dashboard/inventory', requestGet, swrOptions)

    const dashboardData = dashboardResponse?.data ?? null
    const inventoryData = inventoryResponse?.data ?? []

    const { userLocationId } = useUserLocation()
    const location = useLocation()
    const requestedLocation = new URLSearchParams(location.search).get('location_id')
    const currentLocation = navigationLocationId(requestedLocation, userLocationId, isAdmin)
    const { data: navigationViews = [] } = useNavigationViews()

    // Badges
    const fulfillmentOrdersOpenAtLocation =
        dashboardData?.fulfillment_stats_by_location.find(
            (i) => i.id === userLocationId
        )?.fulfillments_orders_to_fulfill

    const productionOrderAtThisLocation =
        dashboardData?.production_orders?.filter(
            (i) => i.location_id === userLocationId
        ) ?? []

    const productionOrdersOpened = sumBy(productionOrderAtThisLocation, 'count')

    const retailerEstimatesAccepted = dashboardData?.retailer_estimates_accepted

    // Warnings
    const atLocationInventoryData = inventoryData.find(
        (i) => i.location.id === userLocationId
    )

    const atLocationInventoryWarning =
        userLocationId !== 1
            ? atLocationInventoryData?.inventory_to_do +
              atLocationInventoryData?.inventory_soon_to_do
            : 0

    // SAV V2
    const serviceItems = [
        {
            icon: HelpSquare,
            label: 'Service après-vente',
            subitems: [
                {
                    icon: Settings04,
                    label: 'Atelier',
                    href: '/service-cases?is_archived=0&is_handled_by=baltic_workshop&columns=received,watchmaker_diagnosis,quote_accepted,quote_refused,repairing,quality_control',
                    pathnames: ['/service-cases', '/service-cases/[id]'],
                },
                {
                    icon: Truck01,
                    label: 'Logistique',
                    href: '/service-cases?is_archived=0&is_handled_by=baltic_workshop&columns=waiting_shipment,in_store_dropoff,to_ship,to_store,with_courier,restocking',
                    pathnames: ['/service-cases', '/service-cases/[id]'],
                },
                {
                    icon: MessageChatCircle,
                    label: 'Service client',
                    href: '/service-cases?is_archived=0&columns=waiting_acceptation,customer_contacted,in_store_dropoff,quote_to_prepare,quote_sent,ready_in_store,pending_swap,pending_refund',
                    pathnames: ['/service-cases', '/service-cases/[id]'],
                },
                {
                    icon: XCircle,
                    label: 'Demandes d’annulation',
                    href: '/orders/cancellation-requests',
                },
                {
                    icon: PlusCircle,
                    label: 'Créer un SAV',
                    href: '/service-cases/create',
                },
                {
                    icon: GitBranch01,
                    label: 'Workflow',
                    href: '/service-cases/workflow',
                },
            ],
        },
    ]

    // Items
    const operationItems = [
        {
            icon: Home01,
            label: 'Dashboard',
            href: '/',
        },
        {
            icon: Package,
            label: 'Préparations',
            viewTableKey: 'fulfillment-orders/table',
            tablePath: '/fulfillment_orders',
            badge: fulfillmentOrdersOpenAtLocation,
            subitems: [
                {
                    icon: CalendarCheck01,
                    label: 'Dates de préparation',
                    href: '/fulfillment_orders/dispatch',
                },
                {
                    icon: CheckDone01,
                    label: 'Préparations effectuées',
                    href: '/fulfillments',
                },
                {
                    icon: PlusCircle,
                    label: 'Créer une demande',
                    href: '/fulfillment_orders/create',
                },
                ...(isAdmin
                    ? [
                          {
                              icon: SwitchHorizontal01,
                              label: 'Déplaçables',
                              href: '/fulfillment_orders/reallocation',
                          },
                      ]
                    : []),
            ],
        },
        ...(isAdmin ? serviceItems : []),
        {
            icon: Container,
            label: 'Stocks',
            viewTableKey: 'components',
            tablePath: '/components',
            warning: atLocationInventoryWarning,
            subitems: [
                {
                    icon: SwitchHorizontal01,
                    label: 'Mouvements de stock',
                    href: '/components/history?perPage=100&invtype=physical',
                    pathnames: ['/components/history'],
                },
                {
                    icon: RefreshCw01,
                    label: 'Demande de réassort',
                    href:
                        '/stock/replenishment-request?location_id=' +
                        userLocationId +
                        '&coverage=2',
                    pathnames: ['/stock/replenishment-request'],
                },
            ],
        },
        {
            icon: Clock,
            label: 'Assemblages',
            viewTableKey: 'production-orders',
            tablePath: '/assembly',
            badge: productionOrdersOpened > 0 ? productionOrdersOpened : null,
            pathnames: ['/assembly/[production_order_id]'],
            subitems: [
                {
                    icon: PlusCircle,
                    label: 'Créer un assemblage',
                    href: '/assembly/create',
                },
            ],
        },
        {
            icon: CreditCard01,
            label: 'Garanties',
            subitems: [
                {
                    icon: Scan,
                    label: 'Associer une garantie',
                    href: '/warranties/associate',
                },
                {
                    icon: List,
                    label: 'Liste des garanties',
                    href: '/warranties',
                },
            ],
        },
        {
            icon: LogIn01,
            label: 'Bons de commande',
            viewTableKey: 'purchase-orders',
            tablePath: '/purchase_orders',
            subitems: [
                {
                    icon: PlusCircle,
                    label: 'Créer un bon de commande',
                    href: '/purchase_orders/create',
                },
            ],
        },
    ]

    const commerceItems = [
        {
            icon: ShoppingCart01,
            label: 'Revendeurs',
            badge: retailerEstimatesAccepted,
            subitems: [
                {
                    icon: File06,
                    label: 'Devis',
                    href: '/retailer_estimates?status=accepted,draft,sent,expired,read,invoiced,advanced,partialinvoiced',
                    pathnames: [
                        '/retailer_estimates',
                        '/retailer_estimates/[id]',
                        '/retailer_estimates/create',
                    ],
                },
                {
                    icon: Receipt,
                    label: 'Factures',
                    href: '/retailer_invoices',
                },
                {
                    icon: ShoppingBag01,
                    label: 'Liste des revendeurs',
                    href: '/retailers',
                    pathnames: ['/retailers', '/retailers/[retailer_id]'],
                },
                {
                    icon: BarChart01,
                    label: 'Statistiques',
                    href: '/retailers/stats',
                },
            ],
        },
    ]

    const supportItems = [
        {
            icon: AiRingIcon,
            label: 'Support IA',
            subitems: [
                {
                    icon: File06,
                    label: 'Fiche de poste',
                    href: '/support/ai/policies',
                    pathnames: ['/support/ai/policies'],
                },
                {
                    icon: BarChart01,
                    label: 'Statistiques',
                    href: '/support/ai/stats',
                    pathnames: ['/support/ai/stats'],
                },
                {
                    icon: MessageChatCircle,
                    label: 'Conversations',
                    href: '/support/ai/conversations',
                    pathnames: ['/support/ai/conversations'],
                },
            ],
        },
    ]

    const adminItems = [
        {
            icon: Settings01,
            label: 'Administration',
            subitems: [
                {
                    icon: Users01,
                    label: 'Utilisateurs',
                    href: '/profiles?disabled=false',
                    pathnames: ['/profiles', '/profiles/[profile_id]'],
                },
                {
                    icon: Tag01,
                    label: 'Produits Shopify',
                    href: '/products/shopify?status=ACTIVE',
                    pathnames: [
                        '/products/shopify',
                        '/products/shopify/[product_id]',
                    ],
                },
                {
                    icon: Tag03,
                    label: 'Variantes Shopify',
                    href: '/products/shopify/variants',
                },
                {
                    icon: Database01,
                    label: 'Sauvegardes',
                    href: '/backups',
                },
            ],
        },
    ]

    const items = [
        ...operationItems,
        ...(userRole === 'ADMIN'
            ? [...commerceItems, ...supportItems, ...adminItems]
            : []),
    ]

    const inserted = new Set()
    const viewItem = (view) => ({
        icon: List, label: view.name, href: tableViewNavigationUrl(view, currentLocation),
        pathnames: [tableViewRoutes[view.table_key]], viewId: String(view.id),
    })
    const withViews = items.map((item) => {
        if (item.viewTableKey) {
            const views = navigationViews.filter((view) => view.table_key === item.viewTableKey)
            views.forEach((view) => inserted.add(view.id))
            const entries = views.length ? views.map(viewItem) : [{
                icon: List, label: 'Toutes les entrées', href: `${item.tablePath}?location_id=${currentLocation}`, pathnames: [item.tablePath],
            }]
            return { ...item, pathnames: [item.tablePath], subitems: [...entries, ...item.subitems] }
        }
        return { ...item, subitems: item.subitems?.flatMap((subitem) => {
            const path = subitem.href?.split('?')[0]
            const views = navigationViews.filter((view) => tableViewRoutes[view.table_key] === path && !inserted.has(view.id))
            views.forEach((view) => inserted.add(view.id))
            return [subitem, ...views.map(viewItem)]
        }) }
    })
    const remaining = navigationViews.filter((view) => !inserted.has(view.id) && tableViewRoutes[view.table_key])
    if (remaining.length) withViews.push({ icon: List, label: 'Vues enregistrées', subitems: remaining.map(viewItem) })
    return withViews
}
